/**
 * Titre du document dans l'en-tête du canvas, renommable au double-clic.
 *
 * Pourquoi le double-clic et pas un bouton « Renommer » : c'est le geste que
 * l'avocat connaît de l'explorateur de fichiers et de Word. Un bouton coûterait
 * une place que l'en-tête n'a pas (cf. ticket #826 : à la largeur réelle du
 * panneau, le libellé se tronquait déjà à quelques caractères).
 *
 * Le titre n'est pas qu'un ornement : c'est lui que l'avocat cherchera dans
 * « Copier un brouillon d’une autre conversation », où tous les documents s'appellent aujourd'hui
 * « Document sans titre ».
 */
import * as React from 'react';
import { cn } from '@/lib/utils';

interface CanvasTitleProps {
  /** Ce qui s'affiche : titre choisi par l'avocat, sinon libellé dérivé de l'état. */
  displayedLabel: string;
  /** Titre choisi par l'avocat, `null` s'il n'a jamais renommé. */
  customTitle: string | null;
  onRename: (title: string | null) => void;
}

export function CanvasTitle({ displayedLabel, customTitle, onRename }: CanvasTitleProps) {
  const [editing, setEditing] = React.useState(false);
  const [value, setValue] = React.useState('');
  const inputRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    if (editing) {
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [editing]);

  const startEditing = () => {
    // On pré-remplit avec le titre CHOISI, pas le libellé affiché : proposer
    // « Nouveau document » (ou « Rédaction de l'acte en cours… ») comme point de
    // départ obligerait à tout effacer avant de pouvoir taper.
    setValue(customTitle ?? '');
    setEditing(true);
  };

  const commit = () => {
    const trimmed = value.trim();
    setEditing(false);
    // Vider le champ REVIENT au libellé automatique — c'est la seule façon de
    // défaire un renommage, et ça évite un document nommé « ».
    const next = trimmed.length > 0 ? trimmed : null;
    if (next !== customTitle) onRename(next);
  };

  if (editing) {
    return (
      <input
        ref={inputRef}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') { e.preventDefault(); commit(); }
          if (e.key === 'Escape') { e.preventDefault(); setEditing(false); }
        }}
        maxLength={255}
        placeholder="Nom du document"
        aria-label="Renommer le document"
        className={cn(
          'flex-1 min-w-0 h-6 px-1.5 text-xs text-stone-900 bg-white rounded',
          'border border-stone-300 outline-none focus:border-stone-500',
        )}
      />
    );
  }

  return (
    <span
      role="button"
      tabIndex={0}
      onDoubleClick={startEditing}
      onKeyDown={(e) => {
        // Parité clavier : le double-clic ne doit pas être le SEUL chemin.
        if (e.key === 'Enter' || e.key === 'F2') { e.preventDefault(); startEditing(); }
      }}
      title={`${displayedLabel}. Double-cliquer pour renommer`}
      aria-label={`Document « ${displayedLabel} ». Double-cliquer ou appuyer sur Entrée pour renommer.`}
      className={cn(
        // PAS `flex-1` (#853) : une base nulle empêcherait le titre de réclamer
        // la largeur de son contenu — il se contenterait du reste et se
        // tronquerait alors même qu'il y avait la place. Base fondée sur le
        // contenu ; c'est `ml-auto` sur l'indicateur d'enregistrement qui absorbe
        // la place libre du bandeau (la pastille du porteur, qui cédait cette
        // largeur avant #1333, n'existe plus).
        'min-w-0 truncate cursor-text rounded px-1 -mx-1 py-0.5',
        'hover:bg-stone-100 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-stone-400',
        customTitle && 'text-stone-900 font-medium',
      )}
    >
      {displayedLabel}
    </span>
  );
}
